import { useState, useRef, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WaitlistModal({ open, onOpenChange, events, event, onSuccess }) {
  const [form, setForm] = useState({ fullName: "", email: "", phoneNumber: "", age: "", city: "Pune", preferredArea: "", instagramHandle: "", linkedinProfile: "", eventId: "" });
  const [consent, setConsent] = useState({ terms: false, age18: false, whatsapp: false, marketing: false });
  const [loading, setLoading] = useState(false);
  const [realtimePhoto, setRealtimePhoto] = useState("");
  const [uploadedPhoto, setUploadedPhoto] = useState("");
  
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const selectedEventId = event?.id || form.eventId;

  useEffect(() => {
    let stream = null;
    if (isCameraOpen) {
      navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } })
        .then(s => {
          stream = s;
          if (videoRef.current) {
            videoRef.current.srcObject = s;
          }
        })
        .catch(err => {
          toast.error("Could not access camera. Please check permissions.");
          setIsCameraOpen(false);
        });
    }
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [isCameraOpen]);

  useEffect(() => {
    if (!open) {
      setIsCameraOpen(false);
    }
  }, [open]);

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      setRealtimePhoto(dataUrl);
      setIsCameraOpen(false);
    }
  };

  const handlePhoto = (e, setter) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setter(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const areas = ["Koregaon Park", "Viman Nagar", "Baner", "Kalyani Nagar", "Aundh", "No preference"];

  const uploadImageToHost = async (base64Str) => {
    if (!base64Str) return "";
    const uploadUrl = process.env.REACT_APP_IMAGE_UPLOAD_URL;
    if (!uploadUrl) return base64Str;
    
    try {
      const res = await fetch(base64Str);
      const blob = await res.blob();
      const formData = new FormData();
      formData.append('image', blob, 'image.jpg');
      
      const uploadRes = await axios.post(uploadUrl, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (uploadRes.data.success) {
        return uploadRes.data.url;
      }
      throw new Error(uploadRes.data.message || "Failed to upload image");
    } catch (err) {
      console.error("Image upload error:", err);
      // Fallback to sending base64 to backend if external upload fails
      return base64Str;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fullName || !form.email || !form.phoneNumber) {
      toast.error("Please fill in all required fields"); return;
    }
    if (!realtimePhoto) { toast.error("Please capture your realtime photo"); return; }
    if (!uploadedPhoto) { toast.error("Please upload a photo for verification"); return; }
    if (!consent.terms) { toast.error("Please agree to the Terms & Conditions and Privacy Policy"); return; }
    if (!consent.age18) { toast.error("Please confirm you are 18 years or older"); return; }
    if (!consent.whatsapp) { toast.error("Please agree to receive WhatsApp updates"); return; }

    setLoading(true);
    try {
      const finalRealtimePhoto = await uploadImageToHost(realtimePhoto);
      const finalUploadedPhoto = await uploadImageToHost(uploadedPhoto);

      const res = await axios.post(`${API}/waitlist`, {
        fullName: form.fullName,
        email: form.email,
        phoneNumber: form.phoneNumber,
        whatsappNumber: form.phoneNumber,
        whatsappOptIn: consent.whatsapp,
        gender: "female",
        age: form.age ? parseInt(form.age) : null,
        city: form.city || "Pune",
        eventId: selectedEventId,
        preferredArea: form.preferredArea,
        instagramHandle: form.instagramHandle,
        linkedinProfile: form.linkedinProfile,
        realtimePhoto: finalRealtimePhoto,
        uploadedPhoto: finalUploadedPhoto,
      });

      const isBooking = res.data.isBooking;
      let msg = "";
      if (isBooking) {
        msg = res.data.whatsappSent
          ? "Spot booked! Confirmation sent on WhatsApp."
          : "Your registration has been received. Confirmation will be sent on WhatsApp shortly.";
      } else {
        msg = res.data.whatsappSent
          ? "You're on the waitlist! Confirmation sent on WhatsApp."
          : "Your registration has been received. Confirmation will be sent on WhatsApp shortly.";
      }
      toast.success(msg);
      setForm({ fullName: "", email: "", phoneNumber: "", age: "", city: "Pune", preferredArea: "", instagramHandle: "", linkedinProfile: "", eventId: "" });
      setConsent({ terms: false, age18: false, whatsapp: false, marketing: false });
      setRealtimePhoto("");
      setUploadedPhoto("");
      setIsCameraOpen(false);
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-card border-border/40 max-h-[90vh] overflow-y-auto" data-testid="waitlist-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight font-medium">Join the women's waitlist</DialogTitle>
          <DialogDescription className="text-muted-foreground">Free entry for women. We'll notify you before each event.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {!event && events && events.length > 0 && (
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Select event</label>
              <select value={form.eventId || ""} onChange={(e) => setForm({ ...form, eventId: e.target.value })}
                className="flex h-9 w-full rounded-sm border border-input bg-transparent px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                data-testid="waitlist-event-select">
                <option value="">Choose an event (Optional)...</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>{ev.title || ev.name} — {ev.venueArea || ev.area} ({ev.eventDate || ev.date})</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">Full name *</label>
            <Input value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })}
              placeholder="Your full name" className="rounded-sm" data-testid="waitlist-name-input" />
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">Email *</label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="you@example.com" className="rounded-sm" data-testid="waitlist-email-input" />
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">Phone / WhatsApp number *</label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground font-medium whitespace-nowrap">+91</span>
              <Input type="tel" value={form.phoneNumber} onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
                placeholder="9876543210" className="rounded-sm" data-testid="waitlist-phone-input" />
            </div>
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-sm font-medium text-foreground mb-1.5 block">Age</label>
              <Input type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })}
                placeholder="25" className="rounded-sm" data-testid="waitlist-age-input" />
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium text-foreground mb-1.5 block">City</label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                placeholder="Pune" className="rounded-sm" data-testid="waitlist-city-input" />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">Preferred area</label>
            <select value={form.preferredArea} onChange={(e) => setForm({ ...form, preferredArea: e.target.value })}
              className="flex h-9 w-full rounded-sm border border-input bg-transparent px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              data-testid="waitlist-area-select">
              <option value="">Choose preferred area...</option>
              {areas.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-sm font-medium text-foreground mb-1.5 block">Instagram</label>
              <Input value={form.instagramHandle} onChange={(e) => setForm({ ...form, instagramHandle: e.target.value })}
                placeholder="@handle" className="rounded-sm" data-testid="waitlist-instagram-input" />
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium text-foreground mb-1.5 block">LinkedIn</label>
              <Input value={form.linkedinProfile} onChange={(e) => setForm({ ...form, linkedinProfile: e.target.value })}
                placeholder="linkedin.com/in/..." className="rounded-sm" data-testid="waitlist-linkedin-input" />
            </div>
          </div>

          {/* Identity Verification */}
          <div className="pt-4 border-t border-border/30">
            <h3 className="text-base font-semibold mb-2">Identity Verification</h3>
            <p className="text-sm text-muted-foreground mb-4">Please capture a live photo and upload an existing photo to verify your identity.</p>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">Live Photo Capture *</label>
                {!isCameraOpen && !realtimePhoto && (
                  <Button type="button" variant="outline" className="w-full" onClick={() => setIsCameraOpen(true)}>
                    Open Camera
                  </Button>
                )}
                
                {isCameraOpen && (
                  <div className="flex flex-col gap-3">
                    <div className="relative w-full rounded-md overflow-hidden border bg-black flex justify-center">
                      <video ref={videoRef} autoPlay playsInline className="w-full max-h-60 object-cover" />
                    </div>
                    <div className="flex gap-2">
                      <Button type="button" className="flex-1" onClick={capturePhoto}>Capture</Button>
                      <Button type="button" variant="outline" className="flex-1" onClick={() => setIsCameraOpen(false)}>Cancel</Button>
                    </div>
                  </div>
                )}

                {realtimePhoto && !isCameraOpen && (
                  <div className="mt-2 relative inline-block text-center w-full">
                    <img src={realtimePhoto} alt="Live Capture" className="w-full max-h-40 object-contain rounded-md border" />
                    <Button type="button" variant="destructive" size="sm" className="absolute top-2 right-2 h-6 w-6 p-0 rounded-full flex items-center justify-center" onClick={() => setRealtimePhoto("")}>&times;</Button>
                  </div>
                )}
                <canvas ref={canvasRef} className="hidden" />
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">Upload Photo *</label>
                <div className="flex flex-col gap-3">
                  <Button type="button" variant="outline" className="w-full" onClick={() => document.getElementById('waitlist-photo-upload').click()}>
                    Choose File
                  </Button>
                  <input type="file" id="waitlist-photo-upload" accept="image/*" className="hidden" onChange={(e) => handlePhoto(e, setUploadedPhoto)} />
                  {uploadedPhoto && (
                    <div className="mt-2 relative inline-block text-center w-full">
                      <img src={uploadedPhoto} alt="Uploaded" className="w-full max-h-40 object-contain rounded-md border" />
                      <Button type="button" variant="destructive" size="sm" className="absolute top-2 right-2 h-6 w-6 p-0 rounded-full flex items-center justify-center" onClick={() => setUploadedPhoto("")}>&times;</Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Consent */}
          <div className="space-y-3 pt-4 border-t border-border/30" data-testid="waitlist-consent">
            <div className="flex items-start gap-3">
              <Checkbox id="wl-wa" checked={consent.whatsapp} onCheckedChange={(c) => setConsent({ ...consent, whatsapp: !!c })} className="mt-0.5" data-testid="waitlist-whatsapp-checkbox" />
              <label htmlFor="wl-wa" className="text-sm leading-relaxed text-muted-foreground cursor-pointer">I agree to receive booking and event updates on WhatsApp. *</label>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox id="wl-terms" checked={consent.terms} onCheckedChange={(c) => setConsent({ ...consent, terms: !!c })} className="mt-0.5" data-testid="waitlist-terms-checkbox" />
              <label htmlFor="wl-terms" className="text-sm leading-relaxed text-muted-foreground cursor-pointer">
                I agree to the <a href="/terms" target="_blank" className="text-foreground underline underline-offset-2">Terms & Conditions</a> and <a href="/privacy" target="_blank" className="text-foreground underline underline-offset-2">Privacy Policy</a>. *
              </label>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox id="wl-age" checked={consent.age18} onCheckedChange={(c) => setConsent({ ...consent, age18: !!c })} className="mt-0.5" data-testid="waitlist-age-checkbox" />
              <label htmlFor="wl-age" className="text-sm leading-relaxed text-muted-foreground cursor-pointer">I confirm I am 18 years or older. *</label>
            </div>
            <div className="flex items-start gap-3">
              <Checkbox id="wl-mkt" checked={consent.marketing} onCheckedChange={(c) => setConsent({ ...consent, marketing: !!c })} className="mt-0.5" data-testid="waitlist-marketing-checkbox" />
              <label htmlFor="wl-mkt" className="text-sm leading-relaxed text-muted-foreground cursor-pointer">I agree to receive promotional communication from Cita Rush.</label>
            </div>
          </div>

          <Button type="submit" disabled={loading || !consent.terms || !consent.age18 || !consent.whatsapp}
            className="w-full rounded-sm px-8 py-5 text-base font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5"
            data-testid="waitlist-submit-btn">
            {loading ? "Joining..." : "Join waitlist — Free"}
          </Button>

          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            By registering, you agree to our <a href="/terms" target="_blank" className="underline underline-offset-2">Terms</a>, <a href="/privacy" target="_blank" className="underline underline-offset-2">Privacy Policy</a>, and <a href="/refund-policy" target="_blank" className="underline underline-offset-2">Refund / No-Match Policy</a>.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
